import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import * as THREE from 'three';
import { loadNews, currentIndex, feed, newsHoverEnter, newsHoverLeave } from '../services/newsPodcast';

// NewsTv — a flat wall-mounted flatscreen playing the platform's News podcast as
// a slideshow (the visual_timeline cues), SYNCED to the shared wall clock so
// every TV in the world shows the same slide at the same moment. Muted/ambient:
// it's a screen glowing in the background, no audio. Drop it anywhere with a
// position + yaw; `width` sets the diagonal-ish size (16:9).
//
//   <NewsTv position={[x,y,z]} rotationY={Math.PI} width={2.4} />
//
// One shared feed (services/newsPodcast) drives them all.
export default function NewsTv({ position = [0, 3, 0], rotationY = 0, width = 2.4 }) {
  const h = (width * 9) / 16;
  const [ready, setReady] = useState(false);
  const [tex, setTex] = useState(null);
  const [aspect, setAspect] = useState(16 / 9);
  const idxRef = useRef(-1);
  const fadeRef = useRef(1);
  const pollRef = useRef(0);
  const cacheRef = useRef(new Map());   // url → THREE.Texture
  const matRef = useRef(null);
  const loaderRef = useRef(null);
  const hoverRef = useRef(false);

  useEffect(() => {
    let dead = false;
    loadNews().then(() => { if (!dead) setReady(true); }).catch(() => {});
    return () => { dead = true; };
  }, []);

  // If we unmount (e.g. leaving the room) while hovered, release the hover so the
  // shared audio doesn't stay stuck on.
  useEffect(() => () => { if (hoverRef.current) { hoverRef.current = false; newsHoverLeave(); document.body.style.cursor = 'auto'; } }, []);

  const onOver = (e) => { e.stopPropagation(); if (!hoverRef.current) { hoverRef.current = true; document.body.style.cursor = 'pointer'; newsHoverEnter(); } };
  const onOut = (e) => { e.stopPropagation(); if (hoverRef.current) { hoverRef.current = false; document.body.style.cursor = 'auto'; newsHoverLeave(); } };

  const getTexture = (url) => {
    const cache = cacheRef.current;
    if (cache.has(url)) return Promise.resolve(cache.get(url));
    if (!loaderRef.current) { loaderRef.current = new THREE.TextureLoader(); loaderRef.current.setCrossOrigin('anonymous'); }
    return new Promise((resolve) => {
      loaderRef.current.load(
        url,
        (t) => { t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 4; cache.set(url, t); resolve(t); },
        undefined,
        () => resolve(null),
      );
    });
  };

  const applyCue = (i) => {
    const f = feed();
    const cue = f?.cues?.[i];
    if (!cue) return;
    getTexture(cue.url).then((t) => {
      if (!t) return;
      if (t.image?.width) setAspect(t.image.width / t.image.height);
      fadeRef.current = 0;        // fade the new slide in
      setTex(t);
    });
  };

  // Shared-clock poll: a few times a second, adopt whatever cue the world clock
  // says is current. Cheap; no per-frame texture work.
  useFrame((_, dt) => {
    if (ready) {
      pollRef.current += dt;
      if (pollRef.current > 0.3) {
        pollRef.current = 0;
        const i = currentIndex();
        if (i >= 0 && i !== idxRef.current) { idxRef.current = i; applyCue(i); }
      }
    }
    if (matRef.current && fadeRef.current < 1) {
      fadeRef.current = Math.min(1, fadeRef.current + dt * 2.2); // ~0.45s crossfade-in
      matRef.current.opacity = fadeRef.current;
    }
  });

  // "Contain" the slide inside the 16:9 screen (article images are often not 16:9).
  const frameAspect = 16 / 9;
  const inW = aspect >= frameAspect ? width : h * aspect;
  const inH = aspect >= frameAspect ? width / aspect : h;
  const title = useMemo(() => (ready ? (feed()?.title || 'The News') : ''), [ready, tex]);

  return (
    <group position={position} rotation={[0, rotationY, 0]}>
      {/* Invisible hover pad over the whole TV — roll ON plays the news narration
          (synced), roll OFF mutes it (barks-style). */}
      <mesh position={[0, 0, 0.06]} onPointerOver={onOver} onPointerOut={onOut}>
        <planeGeometry args={[width + 0.18, h + 0.18]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
      {/* Bezel */}
      <mesh position={[0, 0, -0.04]}>
        <planeGeometry args={[width + 0.18, h + 0.18]} />
        <meshStandardMaterial color="#050506" roughness={0.7} metalness={0.3} />
      </mesh>
      {/* Black screen backing (letterbox bars) */}
      <mesh position={[0, 0, -0.02]}>
        <planeGeometry args={[width, h]} />
        <meshBasicMaterial color="#000" toneMapped={false} />
      </mesh>
      {/* Live slide */}
      {tex && (
        <mesh>
          <planeGeometry args={[inW, inH]} />
          <meshBasicMaterial ref={matRef} map={tex} transparent toneMapped={false} />
        </mesh>
      )}
      {/* NEWS chip (lower-left) */}
      <group position={[-width / 2 + 0.02, -h / 2 + 0.16, 0.01]}>
        <mesh position={[0.34, 0, 0]}>
          <planeGeometry args={[0.7, 0.22]} />
          <meshBasicMaterial color="#d21f2b" toneMapped={false} />
        </mesh>
        <Text position={[0.34, 0, 0.01]} fontSize={0.12} color="#fff" anchorX="center" anchorY="middle" letterSpacing={0.06}>
          NEWS
        </Text>
      </group>
      {/* Headline strip */}
      {title && (
        <Text position={[0.05, -h / 2 + 0.16, 0.01]} fontSize={0.1} color="#ffe9c4" anchorX="left" anchorY="middle" maxWidth={width - 0.9} outlineWidth={0.004} outlineColor="#000">
          {title}
        </Text>
      )}
      {/* Screen glow into the room */}
      <pointLight position={[0, 0, 0.6]} color="#bcd0ff" intensity={1.6} distance={5} />
    </group>
  );
}
